import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, Paper, Card, CardContent, TextField, Button, Alert,
  LinearProgress, CircularProgress, Tooltip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip,
  Switch, FormControlLabel, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  Paid as CostIcon, DataUsage as TokenIcon, Today as TodayIcon,
  SavingsOutlined as BudgetIcon, WarningAmber as WarnIcon,
  TipsAndUpdatesOutlined as TipIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import AudioRetention from '../components/AudioRetention';
import AiServiceCheck from '../components/AiServiceCheck';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const OP_LABELS = {
  soap: 'Note generation (SOAP)',
  soap_fallback: 'Note generation (fallback)',
  summary: 'Patient summary',
  health_tip: 'Daily health tip',
};

const money = (n) => {
  const v = Number(n || 0);
  if (v === 0) return '$0.00';
  if (v < 0.01) return `$${v.toFixed(4)}`;   // sub-cent amounts shown honestly (not "$0.00")
  return `$${v.toFixed(2)}`;
};
const compact = (n) => Intl.NumberFormat('en', { notation: 'compact' }).format(n || 0);

function StatCard({ icon, label, value, sub, color = '#0f172a' }) {
  return (
    <Card elevation={0} sx={{ height: '100%', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 1 }}>
          {icon}
          <Typography variant="body2" fontWeight={600}>{label}</Typography>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800, color }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function Usage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [budgetInput, setBudgetInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [ht, setHt] = useState(null);          // daily health-tip settings
  const [htSaving, setHtSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [usage, tips] = await Promise.all([
        axios.get(`${API_URL}/api/v1/superadmin/gemini-usage`),
        axios.get(`${API_URL}/api/v1/superadmin/health-tip-settings`),
      ]);
      setData(usage.data);
      setBudgetInput(String(usage.data.monthly_budget_usd ?? 0));
      setHt(tips.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load usage');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveBudget = async () => {
    const val = parseFloat(budgetInput);
    if (Number.isNaN(val) || val < 0) { toast.error('Enter a valid amount (0 = unlimited)'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/v1/superadmin/gemini-usage/budget`, { monthly_budget_usd: val });
      toast.success('Monthly budget updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save budget');
    } finally {
      setSaving(false);
    }
  };

  const saveHealthTip = async () => {
    setHtSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/v1/superadmin/health-tip-settings`, {
        enabled: !!ht.enabled,
        prompt: ht.prompt || '',
        frequency_days: Number(ht.frequency_days) || 1,
      });
      setHt({ ...ht, ...res.data });
      toast.success('Daily health-tip settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setHtSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (!data) return null;

  const mtd = data.month_to_date || { cost: 0, tokens: 0, calls: 0 };
  const budget = Number(data.monthly_budget_usd || 0);
  const pct = data.budget_used_pct;
  const maxDaily = Math.max(...(data.daily || []).map((d) => d.cost), 0.0001);

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: '"Fraunces", Georgia, serif', mb: 0.5 }}>
        AI Usage &amp; Cost
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Gemini ({data.model}) token usage and estimated spend. Costs are estimates — Google billing is authoritative.
      </Typography>

      {data.is_over_budget && (
        <Alert severity="error" icon={<WarnIcon />} sx={{ mb: 3, borderRadius: 2 }}>
          <b>Monthly AI budget reached.</b> AI note generation is currently <b>paused</b> for all clinics.
          Raise the budget below or wait for next month to resume.
        </Alert>
      )}

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<CostIcon fontSize="small" />} label="Spend this month"
            value={money(mtd.cost)} sub={`${mtd.calls} calls`}
            color={data.is_over_budget ? '#b91c1c' : '#0f172a'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<BudgetIcon fontSize="small" />} label="Monthly budget"
            value={budget > 0 ? money(budget) : 'Unlimited'}
            sub={budget > 0 && pct != null ? `${pct}% used` : 'No cap set'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<TodayIcon fontSize="small" />} label="Spend today"
            value={money(data.today?.cost)} sub={`${data.today?.calls || 0} calls`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<TokenIcon fontSize="small" />} label="Tokens this month"
            value={compact(mtd.tokens)} sub="prompt + output" />
        </Grid>
      </Grid>

      {budget > 0 && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress variant="determinate" value={Math.min(pct || 0, 100)}
            color={data.is_over_budget ? 'error' : (pct >= 80 ? 'warning' : 'primary')}
            sx={{ height: 10, borderRadius: 5 }} />
          <Typography variant="caption" color="text.secondary">
            {money(mtd.cost)} of {money(budget)} ({pct}%)
          </Typography>
        </Box>
      )}

      {/* Budget control */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>Monthly budget cap</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          When month-to-date estimated spend reaches this, AI note generation is blocked for every clinic. Set <b>0</b> for unlimited.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" label="USD / month" type="number" value={budgetInput}
            onChange={(e) => setBudgetInput(e.target.value)} sx={{ width: 180 }}
            inputProps={{ min: 0, step: '1' }} />
          <Button variant="contained" onClick={saveBudget} disabled={saving}
            sx={{ bgcolor: '#c9a227', color: '#0f172a', '&:hover': { bgcolor: '#b8941f' } }}>
            {saving ? 'Saving…' : 'Save budget'}
          </Button>
        </Box>
      </Paper>

      {/* Daily Health Tips control (super admin only) */}
      {ht && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <TipIcon fontSize="small" sx={{ color: '#c9a227' }} />
            <Typography variant="subtitle1" fontWeight={700}>Daily Health Tips</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The AI wellness tip on every patient&apos;s dashboard. Turn it off to stop AI generation
            (patients then see a static tip — no AI cost), choose how often a new tip is generated,
            and customize the prompt.
          </Typography>

          <FormControlLabel
            control={<Switch checked={!!ht.enabled} onChange={(e) => setHt({ ...ht, enabled: e.target.checked })} />}
            label={ht.enabled ? 'Enabled — AI-generated' : 'Disabled — static fallback (no AI cost)'}
            sx={{ mb: 2, display: 'block' }}
          />

          <FormControl size="small" sx={{ width: 240, mb: 2 }} disabled={!ht.enabled}>
            <InputLabel id="ht-freq-label">Generate a new tip</InputLabel>
            <Select labelId="ht-freq-label" label="Generate a new tip"
              value={Number(ht.frequency_days) || 1}
              onChange={(e) => setHt({ ...ht, frequency_days: e.target.value })}>
              <MenuItem value={1}>Every day</MenuItem>
              <MenuItem value={2}>Every 2 days</MenuItem>
              <MenuItem value={3}>Every 3 days</MenuItem>
              <MenuItem value={7}>Every week</MenuItem>
              <MenuItem value={14}>Every 2 weeks</MenuItem>
              <MenuItem value={30}>Every month</MenuItem>
            </Select>
          </FormControl>

          <TextField fullWidth multiline minRows={3} label="Prompt sent to the AI" disabled={!ht.enabled}
            placeholder={ht.default_prompt} value={ht.prompt || ''}
            onChange={(e) => setHt({ ...ht, prompt: e.target.value })}
            helperText="Leave blank to use the default prompt. The food/tip/benefit JSON format is always enforced automatically."
            sx={{ mb: 1.5 }} />

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={saveHealthTip} disabled={htSaving}
              sx={{ bgcolor: '#c9a227', color: '#0f172a', '&:hover': { bgcolor: '#b8941f' } }}>
              {htSaving ? 'Saving…' : 'Save health-tip settings'}
            </Button>
            <Button size="small" onClick={() => setHt({ ...ht, prompt: '' })} disabled={!ht.enabled || !ht.prompt}>
              Reset prompt to default
            </Button>
            {ht.last_generated && (
              <Typography variant="caption" color="text.secondary">Last new tip generated: {ht.last_generated}</Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* Daily cost chart (last 30 days) */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Estimated cost — last 30 days</Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 160 }}>
          {(data.daily || []).map((d) => (
            <Tooltip key={d.date} arrow
              title={`${d.date}: ${money(d.cost)} · ${compact(d.tokens)} tokens · ${d.calls} calls`}>
              <Box sx={{
                flex: 1, minWidth: 4,
                height: `${Math.max((d.cost / maxDaily) * 100, d.cost > 0 ? 4 : 1)}%`,
                bgcolor: d.cost > 0 ? '#c9a227' : 'rgba(15,23,42,0.08)',
                borderRadius: '3px 3px 0 0', transition: 'background-color .2s',
                '&:hover': { bgcolor: '#0f172a' },
              }} />
            </Tooltip>
          ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">{data.daily?.[0]?.date}</Typography>
          <Typography variant="caption" color="text.secondary">peak {money(maxDaily)}/day</Typography>
          <Typography variant="caption" color="text.secondary">{data.daily?.[data.daily.length - 1]?.date}</Typography>
        </Box>
      </Paper>

      {/* Breakdown by operation */}
      <Paper elevation={0} sx={{ border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3, overflow: 'hidden' }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ p: 2.5, pb: 1 }}>This month by operation</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Operation</TableCell>
                <TableCell align="right">Calls</TableCell>
                <TableCell align="right">Tokens</TableCell>
                <TableCell align="right">Est. cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.by_operation || []).length === 0 && (
                <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                  No AI usage recorded this month yet.
                </TableCell></TableRow>
              )}
              {(data.by_operation || []).map((r) => (
                <TableRow key={r.operation} hover>
                  <TableCell><Chip size="small" label={OP_LABELS[r.operation] || r.operation} /></TableCell>
                  <TableCell align="right">{r.calls}</TableCell>
                  <TableCell align="right">{compact(r.tokens)}</TableCell>
                  <TableCell align="right">{money(r.cost)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Retention sits with the other platform-wide controls, not on a screen of its
          own — it belongs next to spend and health tips, which are the other things
          set once for everybody. */}
      <AudioRetention />

      {/* On demand only: the check is a real, billed AI call. */}
      <AiServiceCheck />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Rates (per 1M tokens): input {money(data.pricing_per_1m_usd?.input)}, audio {money(data.pricing_per_1m_usd?.audio)},
        output {money(data.pricing_per_1m_usd?.output)}. {data.estimate_note}
      </Typography>
    </Box>
  );
}
